import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { datasetsApi, rawApi, georefApi, projectsApi } from '../lib/api';
import { useToast } from '../components/Toast';
import Layout from '../components/Layout';
import { useOrg } from '../contexts/OrgContext';
import Breadcrumbs from '../components/Breadcrumbs';
import LoadingSkeleton from '../components/LoadingSkeleton';
import SurveyCard from '../components/SurveyCard';
import QcCard from '../components/QcCard';
import VectorTilesCard from '../components/VectorTilesCard';
import RasterCard from '../components/RasterCard';
import { rasterApi, rasterJobState, rasterMapUrl } from '../lib/raster-api';
import { trackJob } from '../lib/jobs-api';
import GeorefDialog from '../components/GeorefDialog';
import TopographyPanel from '../components/TopographyPanel';
import PseudosectionPanel from '../components/pseudosection/PseudosectionPanel';
import BoreholesCard from '../components/boreholes/BoreholesCard';
import InversionResultsCard from '../components/InversionResultsCard';
import { useJob } from '../hooks/useJob';
import { useOpenGlobe } from '../hooks/useOpenGlobe';
import { useFeature } from '../lib/runtimeConfig';
import RenameField from '../components/RenameField';
import { Download, FileText, Calendar, Tag, Map, CheckCircle, Trash2, MapPin, FolderInput, Pencil } from 'lucide-react';

// Electrode geometry export / georeferencing only make sense for resistivity-like surveys.
const RESISTIVITY_METHODS = new Set(['Resistivity', 'Induced Polarization', 'Spontaneous Potential', 'Spectral IP']);
export function isResistivityLike(dataset) {
  return !!dataset && (!!dataset.metadata?.format || RESISTIVITY_METHODS.has(dataset.method_type));
}

function DatasetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reparsing, setReparsing] = useState(false);
  const [reparseJobId, setReparseJobId] = useState(null);
  const [showPseudosection, setShowPseudosection] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [moveTo, setMoveTo] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const globeEnabled = useFeature('globe');
  const rasterEnabled = useFeature('raster');
  const vectorTilesEnabled = useFeature('vector_tiles');
  const rawEnabled = useFeature('raw_resistivity');
  const inversionEnabled = useFeature('inversion');
  const [qcReport, setQcReport] = useState(null); // full report from POST raw/qc (else metadata.qc is shown)
  const [qcRunning, setQcRunning] = useState(false);
  const { openGlobe, opening: openingGlobe } = useOpenGlobe();
  
  const loadDataset = useCallback(async () => {
    try {
      const res = await datasetsApi.get(id);
      setDataset(res.data);
    } catch (error) {
      console.error('Failed to load dataset:', error);
      showToast('Failed to load dataset', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    loadDataset();
  }, [loadDataset]);

  // Projects of the dataset's organization: the Project row and the "Move to project" choices.
  const { selectedOrgId: orgId } = useOrg() || {};
  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    projectsApi.list(orgId)
      .then((res) => { if (!cancelled) setProjects(res.data || []); })
      .catch((e) => console.error('Failed to load projects:', e));
    return () => { cancelled = true; };
  }, [orgId]);

  const handleMove = async () => {
    if (!moveTo) return;
    setMoving(true);
    setMoveError(null);
    try {
      await datasetsApi.update(id, { project_id: moveTo });
      const name = projects.find((p) => p.id === moveTo)?.name;
      showToast(name ? `Moved to ${name}` : 'Dataset moved', 'success');
      setMoveOpen(false);
      setMoveTo('');
      await loadDataset();
    } catch (e) {
      setMoveError(e?.response?.data?.detail || e.message || 'Could not move the dataset');
    } finally {
      setMoving(false);
    }
  };

  // Background raw parse (docs/PROCESSING_JOBS.md): follow the queued job, reload when it settles.
  const settledJobsRef = useRef(new Set());
  const [, bumpSettled] = useState(0);
  const metaJobId = dataset && !dataset.metadata?.format ? dataset.metadata?.parse_job?.id : null;
  const pendingParseJobId = reparseJobId
    || (metaJobId && !settledJobsRef.current.has(metaJobId) ? metaJobId : null)
    || null;
  const { job: parseJob } = useJob(pendingParseJobId, {
    onSettled: (job) => {
      settledJobsRef.current.add(job.id);
      bumpSettled((n) => n + 1);
      setReparseJobId(null);
      if (job.status === 'failed') showToast(job.error || 'Could not parse this file as a resistivity survey', 'error');
      else if (job.status === 'cancelled') showToast('Parsing cancelled', 'info');
      loadDataset();
    },
  });
  const parsing = !!pendingParseJobId && (!parseJob || parseJob.status === 'queued' || parseJob.status === 'running');

  // Raster ingest job (docs/RASTER_SUPPORT.md): follow it inline; it also shows in TaskConsole.
  const [rasterJobId, setRasterJobId] = useState(null);
  const [rasterRequesting, setRasterRequesting] = useState(false);
  const metaRasterJob = dataset ? rasterJobState(dataset.metadata) : null;
  const metaRasterJobId = metaRasterJob?.state === 'processing' && !settledJobsRef.current.has(metaRasterJob.jobId)
    ? metaRasterJob.jobId : null;
  const { job: rasterJob } = useJob(rasterJobId || metaRasterJobId, {
    onSettled: (job) => {
      settledJobsRef.current.add(job.id);
      setRasterJobId(null);
      if (job.status === 'failed') showToast(job.error || 'Raster processing failed', 'error');
      else if (job.status === 'succeeded') showToast('Raster ready', 'success');
      loadDataset();
    },
  });

  const handleProcessRaster = async (body) => {
    setRasterRequesting(true);
    try {
      const res = await rasterApi.ingest(id, body);
      if (res.data?.job) {
        setRasterJobId(res.data.job_id);
        trackJob(res.data.job, { label: `Raster: ${dataset?.name || id}` }).catch(() => {});
      }
      showToast('Raster processing queued', 'info');
    } catch (error) {
      showToast(error?.response?.data?.detail || 'Could not queue raster processing', 'error');
    } finally {
      setRasterRequesting(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await datasetsApi.getDownloadUrl(id);
      const url = res.data.download_url;
      window.open(url, '_blank');
      showToast('Download started', 'success');
    } catch (error) {
      console.error('Failed to get download URL:', error);
      showToast('Failed to download file', 'error');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this dataset? This action cannot be undone.')) {
      return;
    }
    
    try {
      showToast('Deleting dataset...', 'info');
      await datasetsApi.delete(id);
      showToast('Dataset deleted successfully', 'success');
      navigate('/datasets');
    } catch (error) {
      console.error('Failed to delete dataset:', error);
      showToast(error?.response?.data?.detail || 'Failed to delete dataset', 'error');
    }
  };

  const handleReparse = async () => {
    setReparsing(true);
    try {
      const res = await rawApi.parse(id);
      if (res.status === 202 && res.data?.job_id) {
        setReparseJobId(res.data.job_id);
        showToast('Parsing in the background', 'info');
        return;
      }
      await loadDataset();
      showToast('Survey file parsed', 'success');
    } catch (error) {
      console.error('Failed to parse survey file:', error);
      showToast('Could not parse this file as a resistivity survey', 'error');
    } finally {
      setReparsing(false);
    }
  };

  const handleViewOnMap = () => {
    navigate(`/map?dataset=${id}`);
    showToast('Opening in Map View...', 'info');
  };

  // ── Georeferencing (docs/GEOREFERENCING_AND_MODEL_IMPORT.md) ──
  const [georefInfo, setGeorefInfo] = useState(null);
  const [showGeoref, setShowGeoref] = useState(false);
  const [showTopography, setShowTopography] = useState(false);
  const [topoVersion, setTopoVersion] = useState(0);

  const handleOpenGeoref = async () => {
    try {
      const res = await georefApi.get(id);
      setGeorefInfo(res.data);
      setShowGeoref(true);
    } catch (error) {
      showToast(error?.response?.data?.detail || 'Could not read electrode geometry from this dataset', 'error');
    }
  };

  const handleSaveGeoref = async (_georef, spec) => {
    const res = await georefApi.save(id, spec);
    setGeorefInfo(prev => ({ ...prev, georef: res.data.georef, derived: res.data.derived }));
    // Merge into the dataset so the Survey card stops reporting local coordinates.
    setDataset(prev => (prev ? { ...prev, metadata: { ...(prev.metadata || {}), georef: res.data.georef } } : prev));
    setShowGeoref(false);
    if (res.data.warning) showToast(res.data.warning, 'warning');
    else showToast(`Georeference saved (${res.data.georef.crs})`, 'success');
  };

  const handleExportGeometry = async (format) => {
    try {
      const res = await georefApi.exportGeometry(id, format);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataset?.name || 'electrodes'}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showToast('Geometry export failed', 'error');
    }
  };

  const isRawSurvey = rawEnabled && !!(dataset?.metadata?.format || dataset?.metadata?.raw);

  const handleRunQC = async () => {
    if (isRawSurvey) {
      setQcRunning(true);
      showToast('Running QC check...', 'info');
      try {
        const res = await rawApi.runQc(id);
        setQcReport(res.data);
        const d = res.data;
        showToast(`QC complete: ${d.counts?.D ?? 0} of ${d.n_readings} readings in tier D (${Number(d.pct_d ?? 0).toFixed(1)}%)`, 'success');
        loadDataset();
      } catch (error) {
        showToast(error?.response?.data?.detail || 'QC check failed', 'error');
      } finally {
        setQcRunning(false);
      }
      return;
    }
    // No automated QC engine for this dataset type: record the request, never fake a verdict.
    try {
      await datasetsApi.updateQcStatus(id, { qc_status: dataset?.qc_status || 'pending', qc_log: 'QC requested; no automated checks exist for this dataset type' });
      showToast(`No automated QC is available for this dataset type. Status stays "${dataset?.qc_status || 'pending'}".`, 'info');
      loadDataset();
    } catch (error) {
      showToast(error?.response?.data?.detail || 'QC status update failed', 'error');
    }
  };

  const getQCBadgeColor = (status) => {
    const colors = {
      passed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div data-testid="dataset-skeleton" aria-busy="true" aria-label="Loading dataset" className="space-y-6">
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-muted rounded w-1/4"></div>
              <div className="h-8 bg-muted rounded w-1/2"></div>
            </div>
            <LoadingSkeleton type="card" />
            <LoadingSkeleton type="table" />
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!dataset) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Breadcrumbs />
          <div className="text-center py-12">
            <p className="text-muted-foreground">Dataset not found</p>
            <Link to="/datasets" className="text-secondary hover:underline mt-4 inline-block">
              Back to Datasets
            </Link>
          </div>
        </div>
      </Layout>
    );
  }
  
  const latestVersion = dataset.latest_version;
  const resistivityLike = isResistivityLike(dataset);
  
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs lastLabel={dataset.name} />
        
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              {renaming ? (
                <div className="mb-2">
                  <RenameField
                    dataset={dataset}
                    className="text-2xl font-bold"
                    onCancel={() => setRenaming(false)}
                    onSaved={(name) => {
                      setDataset((d) => ({ ...d, name }));
                      setRenaming(false);
                      showToast(`Renamed to ${name}`, 'success');
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-3xl font-bold" data-testid="dataset-title">{dataset.name}</h1>
                  <button
                    type="button"
                    onClick={() => setRenaming(true)}
                    aria-label="Rename dataset"
                    title="Rename dataset"
                    data-testid="rename-button"
                    className="p-1 rounded hover:bg-muted text-muted-foreground"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              )}
              {dataset.description && (
                <p className="text-muted-foreground">{dataset.description}</p>
              )}
            </div>
            <div className="flex gap-2">
              {latestVersion && (
                <>
                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:opacity-90 flex items-center gap-2"
                    data-testid="download-button"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </>
              )}
              <button
                onClick={() => { setMoveOpen((o) => !o); setMoveError(null); }}
                className="px-4 py-2 border border-border rounded-md hover:bg-muted flex items-center gap-2"
                data-testid="move-button"
                aria-expanded={moveOpen}
              >
                <FolderInput className="w-4 h-4" />
                Move to project…
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:opacity-90 flex items-center gap-2"
                data-testid="delete-button"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
              <Link
                to="/datasets"
                className="px-4 py-2 border border-border rounded-md hover:bg-muted"
              >
                Back to Catalog
              </Link>
            </div>
          </div>
        </div>

        {moveOpen && (
          <div className="mb-6 flex flex-wrap items-center gap-2 bg-card border border-border rounded-lg p-4" data-testid="move-panel">
            <label htmlFor="move-project" className="text-sm font-medium">Move to project</label>
            <select
              id="move-project"
              data-testid="move-project-select"
              value={moveTo}
              onChange={(e) => setMoveTo(e.target.value)}
              className="px-3 py-2 border border-border rounded-md bg-background text-sm"
            >
              <option value="">Select a project…</option>
              {projects.filter((p) => p.id !== dataset.project_id).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={handleMove}
              disabled={!moveTo || moving}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
              data-testid="move-confirm"
            >
              {moving ? 'Moving…' : 'Move'}
            </button>
            {moveError && <span role="alert" className="text-sm text-destructive" data-testid="move-error">{moveError}</span>}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Metadata Card */}
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4">Dataset Information</h2>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground mb-1">
                    <Tag className="w-4 h-4 inline mr-1" />
                    Method Type
                  </dt>
                  <dd className="text-sm font-semibold">{dataset.method_type}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Created
                  </dt>
                  <dd className="text-sm">{new Date(dataset.created_at).toLocaleDateString()}</dd>
                </div>
                {dataset.latitude && dataset.longitude && (
                  <>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground mb-1">Latitude</dt>
                      <dd className="text-sm monospace">{dataset.latitude.toFixed(6)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground mb-1">Longitude</dt>
                      <dd className="text-sm monospace">{dataset.longitude.toFixed(6)}</dd>
                    </div>
                  </>
                )}
                <div>
                  <dt className="text-sm font-medium text-muted-foreground mb-1">Project</dt>
                  <dd className="text-sm" data-testid="dataset-project">{projects.find((p) => p.id === dataset.project_id)?.name || '—'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground mb-1">Dataset ID</dt>
                  <dd className="text-sm monospace">{dataset.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground mb-1">QC Status</dt>
                  <dd>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${getQCBadgeColor(dataset.qc_status)}`}
                      title={dataset.status_reason || undefined}
                    >
                      {dataset.qc_status}
                    </span>
                    {dataset.status_reason && (
                      <p className="text-xs text-muted-foreground mt-1" data-testid="status-reason">{dataset.status_reason}</p>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Parsed raw resistivity/IP survey (metadata.format set by the backend readers) */}
            {parsing && (
              <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="survey-parsing">
                <h2 className="text-lg font-semibold mb-2">Parsing…</h2>
                <p className="text-sm text-muted-foreground">
                  {parseJob?.message || (parseJob?.status === 'running' ? 'Parsing the survey file' : 'Queued')}
                  {parseJob?.status === 'running' && parseJob.progress > 0 && ` (${Math.round(parseJob.progress * 100)}%)`}
                </p>
              </div>
            )}
            {rasterEnabled && <RasterCard dataset={dataset} job={rasterJob} processing={rasterRequesting}
              onProcess={handleProcessRaster} onShowOnMap={(style) => navigate(rasterMapUrl(id, style))} onChanged={loadDataset} />}
            {rawEnabled && <SurveyCard metadata={dataset.metadata} onReparse={handleReparse} reparsing={reparsing} onShowOnMap={() => navigate(`/map?dataset=${id}`)}
              onOpenInGlobe={globeEnabled ? () => openGlobe({ datasetId: id, organizationId: dataset.org_id }) : undefined} openingGlobe={openingGlobe}
              onShowPseudosection={() => setShowPseudosection(true)} onTopography={() => setShowTopography(true)} datasetId={dataset.id} topoVersion={topoVersion} />}
            {showTopography && <TopographyPanel dataset={dataset} onClose={() => setShowTopography(false)} onApplied={() => setTopoVersion((v) => v + 1)} />}
            {isRawSurvey && <QcCard qc={qcReport || dataset.metadata?.qc} running={qcRunning} onRun={handleRunQC} />}
            {/* Inversion models stored with this dataset (V.12) */}
            {inversionEnabled && <InversionResultsCard dataset={dataset} onOpen={navigate} showEmpty={isResistivityLike(dataset)} />}
            {rawEnabled && showPseudosection && <PseudosectionPanel datasetId={id} variant="modal" onClose={() => setShowPseudosection(false)} />}
            {/* Large datasets: vector tiles and PMTiles export (docs/VECTOR_TILES.md) */}
            {vectorTilesEnabled && <VectorTilesCard datasetId={id} metadata={dataset.metadata} onChanged={loadDataset} showToast={showToast}
              onOpenMap={(mode) => navigate(`/map?dataset=${id}${mode === 'pmtiles' ? '&tiles=pmtiles' : ''}`)} />}
            {/* Borehole datasets: imported collars/intervals/logs (docs/BOREHOLES.md) */}
            {dataset.method_type === 'Borehole' && (
              <BoreholesCard dataset={dataset} onImported={loadDataset} showToast={showToast}
                onShowOnMap={() => navigate(`/map?boreholes=${id}`)} />
            )}

            {/* File Versions */}
            {latestVersion && (
              <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                <h2 className="text-lg font-semibold mb-4">File Information</h2>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {latestVersion.storage_path.split('/').pop()}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {(latestVersion.file_size / 1024).toFixed(2)} KB • {latestVersion.file_type}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Version {latestVersion.version_number} • Uploaded {new Date(latestVersion.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* QC Log */}
            {dataset.qc_log && (
              <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                <h2 className="text-lg font-semibold mb-4">QC Log</h2>
                <pre className="text-sm bg-muted p-4 rounded overflow-auto">
                  {dataset.qc_log}
                </pre>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h3 className="text-sm font-semibold mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-muted-foreground">Total Versions</div>
                  <div className="text-xl font-bold">
                    {dataset.version_count ?? (latestVersion ? 1 : 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Total Size</div>
                  <div className="text-xl font-bold">
                    {latestVersion ? `${(latestVersion.file_size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <h3 className="text-sm font-semibold mb-4">Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={handleViewOnMap}
                  className="w-full px-4 py-2 text-sm border border-border rounded-md hover:bg-muted transition text-left flex items-center gap-2"
                  data-testid="view-on-map-button"
                >
                  <Map className="w-4 h-4" />
                  View on Map
                </button>
                <button
                  onClick={handleRunQC}
                  disabled={qcRunning}
                  className="w-full px-4 py-2 text-sm border border-border rounded-md hover:bg-muted transition text-left flex items-center gap-2"
                  data-testid="run-qc-button"
                >
                  <CheckCircle className="w-4 h-4" />
                  Run QC Check
                </button>
                {resistivityLike && (
                <button
                  onClick={handleOpenGeoref}
                  className="w-full px-4 py-2 text-sm border border-border rounded-md hover:bg-muted transition text-left flex items-center gap-2"
                  data-testid="georeference-button"
                >
                  <MapPin className="w-4 h-4" />
                  Georeference electrodes
                </button>
                )}
                {(georefInfo?.georef || dataset.metadata?.georef) && (() => {
                  const g = georefInfo?.georef || dataset.metadata.georef;
                  const len = (georefInfo?.derived || g.derived)?.line_length;
                  return (
                    <div className="text-xs text-muted-foreground px-1">
                      Georeferenced: {g.mode} · {g.crs}{len !== undefined && ` · ${Number(len).toFixed(1)} m`}
                    </div>
                  );
                })()}
                {resistivityLike ? (
                <div className="flex gap-2">
                  {['geo', 'trn', 'xlsx'].map(f => (
                    <button key={f} onClick={() => handleExportGeometry(f)}
                      className="flex-1 px-2 py-1 text-xs border border-border rounded-md hover:bg-muted transition"
                      title={`Electrode geometry as .${f} (world coordinates when georeferenced)`}>
                      .{f}
                    </button>
                  ))}
                </div>
                ) : (
                  <div className="text-xs text-muted-foreground px-1" data-testid="resistivity-only-note">
                    Electrode geometry export and georeferencing apply to resistivity/IP surveys only.
                  </div>
                )}
              </div>
            </div>
            {showGeoref && georefInfo && (
              <GeorefDialog
                title={`Georeference — ${dataset.name}`}
                electrodes={georefInfo.electrodes || []}
                geometryKind={georefInfo.geometry_kind || 'line'}
                initialGeoref={georefInfo.georef}
                crsGuess={georefInfo.crs_guess}
                datasetRef={{ id: dataset.id, projectId: dataset.project_id }}
                onNavigate={navigate}
                onSave={handleSaveGeoref}
                onClose={() => setShowGeoref(false)}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default DatasetDetail;
